import { Icons } from '@commenda/kakashi';
import { IChatRoomMessage } from '@commenda/types/src/chat-room/types';
import {
  ChatRoomModel,
  cn,
  printDateString,
  useApp,
} from '@commenda/utils';
import { ChatRoomMessageType } from '@prisma/client';
import { useState } from 'react';
import { EmptyStateV2 } from '../../../components/empty-state/EmptyStateV2';
import { ChatProps } from '../../ChatRoom/types';
import ChatDateDivider from '../ChatDateDivider';
import ChatAvatar from '../ChatAvatar';
import ChatFileCard from '../ChatMessages/ChatFileCard';
import { ChatMessageActionButton } from '../ChatMessages/ChatMessageAction';
import { SenderFirmBadge } from '../SenderFirmBadge';

type PinnedMessageItemProps = {
  message: IChatRoomMessage;
  userId: string;
  onFileDownload: ChatProps['onFileDownload'];
  onMessageClick?: (messageId: string) => void;
  onToggleMessagePin?: (messageId: string) => void;
};

const PinnedMessageItem = ({
  message,
  userId,
  onFileDownload,
  onMessageClick,
  onToggleMessagePin,
}: PinnedMessageItemProps) => {
  const isCurrentUser = message?.user?.id === userId;
  const [isHovered, setIsHovered] = useState(false);

  const senderName =
    message?.user?.name ||
    message?.externalUser?.name ||
    message?.externalUser?.email ||
    'Unknown';
  const truncatedName =
    senderName.length > 24 ? `${senderName.substring(0, 24)}...` : senderName;

  const bubbleBg = isCurrentUser ? 'bg-utility-blue-50' : 'bg-bg-secondary';

  return (
    <div
      className="flex gap-md items-start relative w-full"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Avatar */}
      <div
        className={cn('shrink-0', isCurrentUser ? 'order-last' : 'order-first')}
      >
        <ChatAvatar
          size="lg"
          name={senderName}
          image={message?.user?.avatar}
          colorSeed={message?.user?.id}
        />
      </div>

      {/* Content */}
      <div className="flex-1 flex flex-col gap-sm min-w-0 relative">
        {/* Header */}
        <div
          className={cn(
            'flex gap-md items-center w-full',
            isCurrentUser && 'justify-end'
          )}
        >
          {isCurrentUser && (
            <span className="font-mulish text-xs font-normal leading-[15px] text-text-tertiary whitespace-nowrap">
              {new Date(message.sentAt).toLocaleTimeString([], {
                hour: 'numeric',
                minute: '2-digit',
              })}
            </span>
          )}
          <span className="font-mulish text-sm font-medium leading-5 text-text-primary truncate">
            {truncatedName}
          </span>
          <SenderFirmBadge firmName={message?.user?.firmName} />
          {!isCurrentUser && (
            <span className="font-mulish text-xs font-normal leading-[15px] text-text-tertiary whitespace-nowrap">
              {new Date(message.sentAt).toLocaleTimeString([], {
                hour: 'numeric',
                minute: '2-digit',
              })}
            </span>
          )}
        </div>

        {/* Bubble */}
        <div
          className={cn(
            'flex flex-col gap-md justify-center overflow-visible rounded-xl relative max-w-[85%] min-w-0',
            isCurrentUser ? 'self-end items-end' : 'self-start items-start'
          )}
        >
          {/* Action bar on hover */}
          {isHovered && (
            <div
              className={cn(
                'absolute -top-4 z-10',
                isCurrentUser ? 'left-0' : 'left-0'
              )}
            >
              <div className="flex items-center gap-xs p-xs bg-bg-primary border border-border-secondary rounded-md shadow-xs">
                <ChatMessageActionButton
                  onClick={() => onToggleMessagePin?.(message.id)}
                  tooltip="Unpin message"
                  icon={<Icons.Pin02 className="size-3 text-fg-brand" />}
                  active
                />
                <ChatMessageActionButton
                  onClick={() => onMessageClick?.(message.id)}
                  tooltip="Go to message"
                  icon={
                    <Icons.CornerUpLeft className="size-3 text-fg-quaternary" />
                  }
                />
              </div>
            </div>
          )}

          {message?.message && (
            <div
              className={cn(
                'flex flex-col gap-md items-start p-lg rounded-md',
                bubbleBg
              )}
            >
              <p className="font-mulish text-sm font-normal leading-5 text-text-primary w-full whitespace-pre-wrap break-words">
                {message.message}
              </p>
            </div>
          )}
          {message?.files?.length > 0 && (
            <div className="flex flex-col w-full">
              {message.files.map((file) => (
                <ChatFileCard
                  key={file.id}
                  fileName={file?.fileName}
                  onFileDownload={onFileDownload}
                  fileId={file?.id}
                  fileUrl={file?.fileUrl}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

type PinnedChatViewProps = {
  messages: IChatRoomMessage[];
  onFileDownload: ChatProps['onFileDownload'];
  onMessageClick?: (messageId: string) => void;
  onToggleMessagePin?: (messageId: string) => void;
  userId: string;
};

const PinnedChatView = ({
  messages,
  onFileDownload,
  onMessageClick,
  onToggleMessagePin,
  userId,
}: PinnedChatViewProps) => {
  const pinnedMessages = messages?.filter(
    (message) => message.isPinned && message.pinnedByUserId === userId
  );

  const { appType } = useApp();

  const filteredMessages = ChatRoomModel.canSeeAgentInternalRoom(appType)
    ? pinnedMessages
    : ChatRoomModel.filterCustomerVisible(pinnedMessages);

  const visibleMessages = filteredMessages?.filter(
    (message) => message.type !== ChatRoomMessageType.DOCUMENT_REQUEST
  );

  if (!visibleMessages?.length) {
    return (
      <div className="h-full flex flex-col">
        <EmptyStateV2
          title="No pinned messages"
          description="Any pinned messages will appear here."
          className="bg-white rounded-none border-none"
        />
      </div>
    );
  }

  // Build elements with date dividers
  const elements: JSX.Element[] = [];
  let currentDate = printDateString(visibleMessages[0]?.sentAt);

  elements.push(
    <ChatDateDivider
      key={`divider-start-${visibleMessages[0].id}`}
      date={visibleMessages[0].sentAt}
    />
  );

  visibleMessages.forEach((message) => {
    const messageDate = printDateString(message.sentAt);
    if (messageDate !== currentDate) {
      currentDate = messageDate;
      elements.push(
        <ChatDateDivider
          key={`divider-${message.id}`}
          date={message.sentAt}
        />
      );
    }

    elements.push(
      <PinnedMessageItem
        key={message.id}
        message={message}
        userId={userId}
        onFileDownload={onFileDownload}
        onMessageClick={onMessageClick}
        onToggleMessagePin={onToggleMessagePin}
      />
    );
  });

  return (
    <div className="h-full flex flex-col">
      <div className="flex-1 scroll-smooth overflow-auto">
        <div className="flex flex-col gap-3xl pt-xl px-lg pb-xl">
          {elements}
        </div>
      </div>
    </div>
  );
};

export default PinnedChatView;
